// noinspection JSUnusedGlobalSymbols

import {defineStore, getActivePinia} from "pinia";
import authenticationService from "@/shared/services/authentication.service.ts";
import {
    Account,
    AccountInput,
    AccountResponse,
    AuthenticationResponse,
    Contact,
    Customer,
    CustomerAccount,
    GlobalError,
    Representative,
    SalesRepOnlyAccount
} from "@/shared/graphql/types";
import * as Sentry from "@sentry/vue";
import {getApolloClient} from "@/shared/apolloclient/apollo.client.ts";
//@ts-ignore
import ACCOUNT_UPDATE_MUTATION from "@/shared/graphql/account/account.update.mutation.graphql";
import {extractGraphData} from "@/shared/composable/service.composables.ts";
import {activeLanguage} from "@/shared/i18n/i18n.ts";

// Local storage keys
const AUTH_STORAGE_KEY = 'auth_data';

// Utility functions for localStorage
const localStorageUtil = {
    saveAuthData: (account: Account, app: string) => {
        try {
            localStorage.setItem(`${AUTH_STORAGE_KEY}-${app}`, JSON.stringify(account));
        } catch (error) {
            console.error('Failed to save auth data to localStorage:', error);
        }
    },
    getAuthData: (app: string): Account | null => {
        try {
            const accountData = localStorage.getItem(`${AUTH_STORAGE_KEY}-${app}`);
            return (accountData ? JSON.parse(accountData) : null)
        } catch (error) {
            console.error('Failed to retrieve auth data from localStorage:', error);
            return null;
        }
    },
    clearAuthData: (app: string) => {
        try {
            localStorage.removeItem(`${AUTH_STORAGE_KEY}-${app}`);
        } catch (error) {
            console.error('Failed to clear auth data from localStorage:', error);
        }
    },
    isOnline: (): boolean => {
        return navigator.onLine;
    }
};

export const useAuthenticationStore = defineStore('authenticationStore', {
    state: () => ({
        app: null as string,
        authenticated: false,
        account: null as Account,
        lang: "nl",//Todo dit moeten we bepalen na login
    }),
    getters: {
        isCustomerAccount: (state): boolean => {
            return (state?.account["__typename"] == "CustomerAccount")
        },
        isSalesRepAccount: (state): boolean => {
            return (state?.account["__typename"] == "SalesRepOnlyAccount")
        },
        representative: (state): Representative => {
            if (state.account["__typename"] != "SalesRepOnlyAccount") return null
            return (state?.account as SalesRepOnlyAccount).representative
        },
        customerAccount: (state): CustomerAccount => {
            if (state?.account["__typename"] != "CustomerAccount") throw Error("Not logged in as a customer")
            return state.account as CustomerAccount
        },
        customer: (state): Customer => {
            if (!state.account) throw Error("Not logged-in")
            if (state.account["__typename"] != "CustomerAccount") throw Error("Not logged in as a customer")
            return (state.account as CustomerAccount).customer
        },
        contact: (state): Contact => {
            if (state.account["__typename"] != "CustomerAccount") throw Error("Not logged in as a customer")
            return (state.account as CustomerAccount).contact
        },
        contactSafe: (state): Contact => {
            if (state?.account["__typename"] != "CustomerAccount") return null
            return (state.account as CustomerAccount).contact
        },
    },
    actions: {
        async login(username: string, password: string): Promise<AuthenticationResponse> {
            const response = await authenticationService.login(username, password)
            const authorizedForApp = response.account?.apps?.includes(this.app) || false
            const authenticated = response.validation.success
            this.authenticated = authenticated && authorizedForApp
            if (this.authenticated) {
                this.account = response.account
                // Save an authenticated account to localStorage for offline use
                localStorageUtil.saveAuthData(response.account, this.app);
                setSentryUser.call(this)
            } else {
                response.validation.success = false
                if (authenticated && !authorizedForApp) {
                    response.validation.globalErrors.push({
                        message: "Gebruiker heeft geen toegang tot deze applicatie",
                        key: "authentication.no_access"
                    } as GlobalError)
                }
            }
            return response
        },
        async logout() {
            try {
                const response = await authenticationService.logout();

                // Clear authentication data from localStorage
                localStorageUtil.clearAuthData(this.app);

                this.authenticated = false;
                this.account = null;

                //@ts-ignore
                Sentry.setUser(null);

                // Get the active Pinia instance
                const pinia = getActivePinia();
                if (pinia) {
                    const allStores = Object.entries(pinia.state.value); // Access all active Pinia stores
                    allStores.forEach(([storeName, store]) => {
                        if (storeName === 'tenantStore') {
                            return; // Skip clearing tenantStore
                        }
                        //@ts-ignore - Ignore if $reset is not recognized by TypeScript
                        if (pinia._s.get(storeName)?.$reset) {
                            //@ts-ignore
                            pinia._s.get(storeName)?.$reset(); // Reset the store
                        }
                    });
                } else {
                    console.warn('No active Pinia instance found.');
                }

                return response;
            } catch (error) {
                console.error("Error during logout", error);
                // Still clear local data even if the server logout fails
                localStorageUtil.clearAuthData(this.app);
                this.authenticated = false;
                this.account = null;
                return {success: false};
            }
        },
        async init(app: string): Promise<Account> {
            this.app = app

            // If already authenticated in memory, return the account
            if (this.account != undefined && this.authenticated) {
                setSentryUser.call(this)
                return this.account;
            } else {
                try {
                    // Check if we're online
                    if (localStorageUtil.isOnline()) {
                        // Online: check with server and update localStorage
                        const account = await authenticationService.isAuthenticated();
                        this.authenticated = account != undefined && account.apps.includes(app);

                        if (this.authenticated) {
                            this.account = account;
                            // Update localStorage with latest data
                            localStorageUtil.saveAuthData(account, app);
                        } else {
                            // If not authenticated anymore, clear localStorage
                            localStorageUtil.clearAuthData(this.app);
                        }
                    } else {
                        // Offline: check localStorage for saved authentication data
                        console.log('Working in offline mode, using stored authentication data');
                        const account = localStorageUtil.getAuthData(this.app);

                        if (account) {
                            this.account = account;
                            this.authenticated = account.apps.includes(app);
                        } else {
                            this.authenticated = false;
                            this.account = null;
                        }
                    }

                    setSentryUser.call(this);
                    return this.account;
                } catch (error) {
                    console.error('Authentication initialization error:', error);

                    // Fallback to localStorage if the server check fails
                    const account = localStorageUtil.getAuthData(this.app);
                    if (account) {
                        this.account = account;
                        this.authenticated = account.apps.includes(app);
                        setSentryUser.call(this);
                        return this.account;
                    }

                    this.authenticated = false;
                    return null;
                }
            }
        },
        hasRole(_: String[]) {
            return true
            //Todo manier uitwerken om de rollen op te halen
            // if (roles.length === 0) return true
            // // @ts-ignore
            // const customerRoles = jwt.roles as String[]
            // if (customerRoles == null) return false
            // return roles.some((role) => {
            //     return customerRoles.some((customerRole) => customerRole === role)
            // })
        },
        async update(account: AccountInput) {
            const variables = {
                language: activeLanguage(),
                input: account
            };
            const response = await getApolloClient().mutate({
                mutation: ACCOUNT_UPDATE_MUTATION,
                variables: variables,
                fetchPolicy: 'network-only'
            });
            this.account = response.data.tenant.me.update.account
            return extractGraphData(response.data.tenant.me.update) as AccountResponse;
        }
    }
})
export default useAuthenticationStore
let sentryUser = null

function setSentryUser() {
    //@ts-ignore
    if (this.account && !sentryUser) {
        try {
            const name = this.contactSafe?.firstname ? `, ${this.contactSafe?.firstname}` : this.contactSafe?.fullName ? `, ${this.contactSafe?.fullName}` : ''
            sentryUser = {
                username: this.account?.username ?? "",
                name: name.trim()
            };
            console.log("Setting sentry user", sentryUser)
            Sentry.setUser(sentryUser);
        } catch (e) {
            console.error("could not set Sentry User", e)
        }
    }
}